import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import { IDLE, PENDING, LIST, VIEW, EDIT } from "const"
import {
    replaceHistoryState,
    hasAccess,
    StateType,
} from "client/utils"
import { qidCompaniesRoute } from "client/parts/routes"
import {
    PRM_QID_COMPANY,
    PRM_QID_PRODUCT,
    StoreType,
    QidCompanyProps,
    LoadQidCompanyById,
    setQidCompanyView,
    initQidCompany,
} from "client/store"
import {
    Container,
    PageHeader,
    PageTitle,
    PageContent,
    PageMenu,
    PageMenuButton,
    Tab,
    Collapse,
    Loader,
} from "client/components"

import AccessDenied from "client/parts/accessDenied"
import NotFound from "client/parts/notFound"

import Form from "./form"
import Documents from "../documents"
import Products from "../../product/list"

const DETAILS = "#details"
const DOCUMENTS = "#documents"
const PRODUCTS = "#products"

const menu = [DETAILS, DOCUMENTS, PRODUCTS]

const View = ({
    companyId = null,
    hash = null,
}: QidCompanyProps) => {
    const company = useSelector((state: StoreType) => state.qidCompany.companyView)
    const reloadView = useSelector((state: StoreType) => state.qidCompany.reloadView)

    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [activeTab, setActiveTab] = useState(menu.includes(hash) ? hash : DETAILS)
    const [loadState, setLoadState] = useState<StateType>(IDLE)

    useEffect(() => (
        () => {
            // clear company on unmount
            dispatch(setQidCompanyView(initQidCompany))
        }
    ), [])

    useEffect(() => {
        if (!reloadView) return
        setLoadState(PENDING)
        LoadQidCompanyById(companyId).then((res) => {
            dispatch(setQidCompanyView(res))
            setLoadState(IDLE)
        })
    }, [reloadView])

    const handleMenuClick = (tab: string) => {
        setActiveTab(tab)
        replaceHistoryState(`${qidCompaniesRoute}/${VIEW}/${companyId}${tab}`)
    }

    const companyViewAccess = hasAccess(PRM_QID_COMPANY, VIEW)
    const companyEditAccess = hasAccess(PRM_QID_COMPANY, EDIT)
    const productListAccess = hasAccess(PRM_QID_PRODUCT, LIST)

    if (!companyViewAccess) return <AccessDenied />
    if (!company) return <NotFound />
    return (
        <Container>
            <PageHeader>
                <PageTitle>
                    {
                        companyEditAccess
                            ? `${lang.viewCompany} # ${company.id || ""}` // for Super Admin
                            : company.name // for account holders
                    }
                </PageTitle>
            </PageHeader>
            {
                companyEditAccess && ( // Super Admin view
                    <PageContent>
                        <PageMenu>
                            <PageMenuButton
                                isActive={activeTab === DETAILS}
                                onClick={() => handleMenuClick(DETAILS)}
                            >
                                {lang.details}
                            </PageMenuButton>
                            <PageMenuButton
                                isActive={activeTab === DOCUMENTS}
                                onClick={() => handleMenuClick(DOCUMENTS)}
                            >
                                {lang.documents}
                            </PageMenuButton>
                            {
                                productListAccess && (
                                    <PageMenuButton
                                        isActive={activeTab === PRODUCTS}
                                        onClick={() => handleMenuClick(PRODUCTS)}
                                    >
                                        {lang.products}
                                    </PageMenuButton>
                                )
                            }
                        </PageMenu>

                        <Tab show={activeTab === DETAILS}>
                            {loadState === PENDING && <Loader />}
                            <Form />
                        </Tab>
                        <Tab show={activeTab === DOCUMENTS}>
                            <Documents />
                        </Tab>
                        {
                            productListAccess && (
                                <Tab show={activeTab === PRODUCTS}>
                                    <Products companyId={companyId} />
                                </Tab>
                            )
                        }
                    </PageContent>
                )
            }

            {
                !companyEditAccess && ( // account holder view
                    <PageContent>
                        <Form />

                        <Collapse show title={lang.documents}>
                            <Documents />
                        </Collapse>
                        {
                            productListAccess && (
                                <Collapse show title={lang.products}>
                                    <Products companyId={companyId} />
                                </Collapse>
                            )
                        }
                    </PageContent>
                )
            }
        </Container>
    )
}
export default View
